import React from 'react';
import { Select, Tag, Space } from 'antd';
import { CloudServerOutlined } from '@ant-design/icons';
import { useServerContext } from '../context/ServerContext';
import { BotServerPublic } from '../types/botServer';
import { getServerTypeLabel, getServerTypeTagColor, isMainBot, isWlBot } from '../utils/serverTypeLabels';

function buildGroupedOptions(servers: BotServerPublic[]) {
    const mainBots = servers.filter((s) => isMainBot(s.type));
    const wlBots = servers.filter((s) => isWlBot(s.type));

    const groups: { label: React.ReactNode; options: { value: string; label: string }[] }[] = [];

    if (mainBots.length > 0) {
        groups.push({
            label: <span style={{ fontWeight: 600 }}>Main-bot ({mainBots.length})</span>,
            options: mainBots.map((s) => ({ value: s.serverId, label: s.serverId })),
        });
    }

    if (wlBots.length > 0) {
        groups.push({
            label: <span style={{ fontWeight: 600 }}>WL-bot ({wlBots.length})</span>,
            options: wlBots.map((s) => ({ value: s.serverId, label: s.serverId })),
        });
    }

    return groups;
}

const ServerSelector: React.FC = () => {
    const { servers, selectedServerId, setSelectedServerId, loading } = useServerContext();

    const validServers = servers.filter((s) => s.serverId);

    if (loading || validServers.length === 0) {
        return null;
    }

    const activeId = validServers.some((s) => s.serverId === selectedServerId)
        ? selectedServerId
        : validServers[0].serverId;

    const selected = validServers.find((s) => s.serverId === activeId);

    return (
        <Space style={{ marginRight: 16 }}>
            <CloudServerOutlined style={{ color: '#fff' }} />
            <Select
                value={activeId}
                onChange={setSelectedServerId}
                style={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
                options={buildGroupedOptions(validServers)}
                optionRender={(option) => {
                    const server = validServers.find((s) => s.serverId === option.value);
                    if (!server) return option.label;
                    return (
                        <Space>
                            {server.serverId}
                            <Tag color={getServerTypeTagColor(server.type)} style={{ margin: 0 }}>
                                {getServerTypeLabel(server.type)}
                            </Tag>
                        </Space>
                    );
                }}
            />
            {selected && (
                <Tag color={getServerTypeTagColor(selected.type)} style={{ margin: 0 }}>
                    {getServerTypeLabel(selected.type)}
                </Tag>
            )}
        </Space>
    );
};

export default ServerSelector;
